/**
 * Where a room's name goes.
 *
 * The generator used to pick a square at random, which put names on top of furniture, against
 * walls, and spilling out of the room they belonged to. The room's shape already says where the
 * name should go, so this works it out instead of guessing: find the widest clear horizontal run
 * of the room's own squares, near the middle of it, and centre the name on that.
 *
 * A run is broken by furniture as well as by the room's own edge, so "the widest clear stretch"
 * means squares you can actually write across — not a whole row that merely happens to have a tree
 * at one end. Only if a room is furnished from wall to wall does it fall back to bare rows.
 *
 * Geometry only — nothing here knows about pixels, fonts or SVG. The caller says how many squares
 * wide the name needs; this says which squares to use.
 */

import { colOf, rowOf } from "./grid";
import type { Board, Room } from "./types";

export interface LabelPlacement {
  /** Row the name sits on. */
  row: number;
  /** Centre of the name, in columns. Fractional: a run of two squares centres on the seam. */
  centreColumn: number;
  /** How many squares the run is, so the caller can shrink text that still will not fit. */
  span: number;
  /**
   * Whether the chosen squares are free of furniture. A room can be furnished wall to wall, and
   * then there is no clear spot to find — the caller has to make the name readable instead, with a
   * backing plate. Better than leaving the room unnamed.
   */
  clear: boolean;
}

interface Run {
  row: number;
  start: number;
  end: number;
  clear: boolean;
}

/**
 * Every maximal left-to-right stretch of this room, per row.
 *
 * With `mustBeClear`, furniture breaks a stretch the same way a wall does, so what comes back is
 * the squares a name could actually be written across.
 */
function runsOf(board: Board, room: Room, mustBeClear: boolean): Run[] {
  const cells = new Set(room.cells);
  const runs: Run[] = [];

  for (let row = 0; row < board.size; row++) {
    let start: number | null = null;

    for (let col = 0; col <= board.size; col++) {
      const cell = row * board.size + col;
      const free = col < board.size && (board.objectAt[cell] as number) < 0;
      const usable = col < board.size && cells.has(cell) && (!mustBeClear || free);

      if (usable) {
        if (start === null) start = col;
      } else if (start !== null) {
        runs.push({ row, start, end: col - 1, clear: mustBeClear });
        start = null;
      }
    }
  }
  return runs;
}

/**
 * Pick the best stretch for a name that needs `columnsNeeded` squares.
 *
 * Clear squares always win: a name written over a table is unreadable, and a narrower spot is not.
 * Among stretches you can write across, wide beats narrow, and closer to the middle of the room
 * beats further away — a name at the room's edge reads as if it belongs to the room next door.
 */
export function labelPlacement(board: Board, room: Room, columnsNeeded = 1): LabelPlacement {
  // Furniture-free stretches first; bare rows only for a room furnished from wall to wall.
  const clear = runsOf(board, room, true);
  const runs = clear.length > 0 ? clear : runsOf(board, room, false);
  if (runs.length === 0) {
    // Cannot happen for a room built by the generator, but a caller should still get an answer.
    const cell = room.cells[0] ?? 0;
    return {
      row: rowOf(cell, board.size),
      centreColumn: colOf(cell, board.size) + 0.5,
      span: 1,
      clear: false,
    };
  }

  const rows = room.cells.map((cell) => rowOf(cell, board.size));
  const middleRow = (Math.min(...rows) + Math.max(...rows)) / 2;

  const score = (run: Run): number => {
    const width = run.end - run.start + 1;
    return (width >= columnsNeeded ? 500 : 0) + width * 10 - Math.abs(run.row - middleRow) * 3;
  };

  const best = runs.reduce((a, b) => (score(b) > score(a) ? b : a));
  return {
    row: best.row,
    centreColumn: (best.start + best.end + 1) / 2,
    span: best.end - best.start + 1,
    clear: clear.length > 0,
  };
}
